import { useEffect, useRef, useState } from "react";
import type {
  ShiftProductionForm,
  ShiftProductionFormPayload,
  ShiftProductionFormListItem,
} from "../../../shared/shift-production-form/contracts";
import { ApiError } from "../../shared/api/client";
import {
  getOperatorDraft,
  getOperatorRecentSubmissions,
  reviseOperatorForm,
  saveNewShiftForm,
  saveShiftForm,
  submitShiftForm,
} from "./shift-production-form-gateway";
import {
  beginCreateAttempt,
  createSerialTaskQueue,
  payloadChanged,
  snapshotPayload,
  type PendingCreateAttempt,
} from "./operator-form-persistence";

function assertOwnedBy(operatorId: string, ownerOperatorId: string) {
  if (operatorId !== ownerOperatorId)
    throw new Error(
      "Bu kayıt başka bir operatöre ait. Kayıt kapatılmadan farklı operatör PIN’iyle işlem yapılamaz.",
    );
}

export function useOperatorFormWorkspace() {
  const [recentForms, setRecentForms] = useState<ShiftProductionFormListItem[]>(
    [],
  );
  const [recentOffset, setRecentOffset] = useState(0);
  const [recentTotal, setRecentTotal] = useState(0);
  const [recentBusy, setRecentBusy] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);
  const recentRequest = useRef<AbortController | null>(null);
  const [form, setForm] = useState<ShiftProductionForm | null>(null);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const createKey = useRef(crypto.randomUUID());
  const createAttempt = useRef<PendingCreateAttempt | null>(null);
  const formRef = useRef<ShiftProductionForm | null>(null);
  const saveQueue = useRef(createSerialTaskQueue());
  const pendingSaves = useRef(0);
  const resumeRequest = useRef<AbortController | null>(null);
  const workspaceGeneration = useRef(0);
  useEffect(
    () => () => {
      workspaceGeneration.current += 1;
      resumeRequest.current?.abort();
      recentRequest.current?.abort();
    },
    [],
  );
  const advanceGeneration = () => {
    recentRequest.current?.abort();
    recentRequest.current = null;
    setRecentForms([]);
    setRecentOffset(0);
    setRecentTotal(0);
    setRecentError(null);
    setRecentBusy(false);
    const generation = ++workspaceGeneration.current;
    saveQueue.current = createSerialTaskQueue();
    pendingSaves.current = 0;
    formRef.current = null;
    createAttempt.current = null;
    createKey.current = crypto.randomUUID();
    return generation;
  };
  const resume = async (operatorId: string) => {
    const generation = advanceGeneration();
    resumeRequest.current?.abort();
    const controller = new AbortController();
    resumeRequest.current = controller;
    setBusy(true);
    setError(null);
    try {
      const response = await getOperatorDraft(controller.signal);
      if (
        controller.signal.aborted ||
        generation !== workspaceGeneration.current
      )
        throw new Error("Form açma işlemi iptal edildi.");
      if (response.form)
        assertOwnedBy(operatorId, response.form.ownerOperatorId);
      formRef.current = response.form;
      setForm(response.form);
      setSelectionVersion((value) => value + 1);
      setNotice(response.form ? "Yarım kalan formunuz açıldı." : null);
      return response.form;
    } finally {
      if (
        resumeRequest.current === controller &&
        generation === workspaceGeneration.current
      ) {
        resumeRequest.current = null;
        setBusy(false);
      }
    }
  };
  const startNew = () => {
    advanceGeneration();
    resumeRequest.current?.abort();
    resumeRequest.current = null;
    setBusy(false);
    setForm(null);
    setError(null);
    setNotice(null);
    setSelectionVersion((value) => value + 1);
  };

  const performSave = async (
    payload: ShiftProductionFormPayload,
    submit: boolean,
    operatorId: string,
    generation: number,
  ) => {
    if (generation !== workspaceGeneration.current) return null;
    const currentForm = formRef.current;
    const pendingAttempt = createAttempt.current;
    if (
      !currentForm &&
      pendingAttempt?.operatorId !== undefined &&
      pendingAttempt.operatorId !== operatorId
    ) {
      setError(
        "Bu yeni form için önceki kayıt denemesi başka bir operatöre ait. Önce yeni form açın veya önceki operatör PIN’iyle yeniden deneyin.",
      );
      return null;
    }
    if (currentForm) {
      try {
        assertOwnedBy(operatorId, currentForm.ownerOperatorId);
      } catch (reason) {
        setError(
          reason instanceof Error
            ? reason.message
            : "Kayıt sahibi doğrulanamadı.",
        );
        return null;
      }
    }

    setError(null);
    setNotice(null);
    try {
      let saved;
      if (currentForm) {
        saved = await saveShiftForm(
          currentForm.id,
          currentForm.version,
          payload,
        );
      } else {
        const attempt =
          pendingAttempt ??
          beginCreateAttempt(operatorId, createKey.current, payload);
        createAttempt.current = attempt;
        try {
          saved = await saveNewShiftForm(
            attempt.payload,
            attempt.idempotencyKey,
          );
        } catch (reason) {
          if (
            generation === workspaceGeneration.current &&
            !isCreateOutcomeUncertain(reason)
          ) {
            createAttempt.current = null;
            createKey.current = crypto.randomUUID();
          }
          throw reason;
        }
        if (generation !== workspaceGeneration.current) return null;
        assertOwnedBy(operatorId, saved.form.ownerOperatorId);
        formRef.current = saved.form;
        setForm(saved.form);
        createAttempt.current = null;
        createKey.current = crypto.randomUUID();
        if (payloadChanged(attempt.payload, payload)) {
          saved = await saveShiftForm(
            saved.form.id,
            saved.form.version,
            payload,
          );
        }
      }
      if (generation !== workspaceGeneration.current) return null;
      assertOwnedBy(operatorId, saved.form.ownerOperatorId);
      formRef.current = saved.form;
      setForm(saved.form);

      const next = submit
        ? await submitShiftForm(saved.form.id, saved.form.version)
        : saved;
      if (generation !== workspaceGeneration.current) return null;
      assertOwnedBy(operatorId, next.form.ownerOperatorId);
      formRef.current = submit ? null : next.form;
      setForm(submit ? null : next.form);
      if (submit) {
        setSelectionVersion((value) => value + 1);
        advanceGeneration();
        setBusy(false);
      }
      setNotice(
        submit
          ? `Formunuz kaydedildi. (${next.form.formNo})`
          : null,
      );
      return next.form;
    } catch (reason) {
      if (generation !== workspaceGeneration.current) return null;
      if (
        reason instanceof ApiError &&
        reason.code === "OPERATOR_SESSION_REQUIRED"
      )
        throw reason;
      if (reason instanceof ApiError && reason.code === "VERSION_CONFLICT") {
        setError(
          "Sunucudaki kayıt sürümü değişti. Yazdıklarınız korunuyor; kontrol için sistem yöneticisine başvurun.",
        );
      } else {
        setError(
          reason instanceof Error
            ? reason.message
            : "Form kaydedilemedi. Yazdıklarınız bu ekranda korunuyor.",
        );
      }
      return null;
    }
  };

  const save = (
    payload: ShiftProductionFormPayload,
    submit: boolean,
    operatorId: string,
  ) => {
    const generation = workspaceGeneration.current;
    const queuedPayload = snapshotPayload(payload);
    pendingSaves.current += 1;
    setBusy(true);
    const queued = saveQueue.current.run(() =>
      performSave(queuedPayload, submit, operatorId, generation),
    );
    return queued.finally(() => {
      if (generation !== workspaceGeneration.current) return;
      pendingSaves.current -= 1;
      if (pendingSaves.current === 0) setBusy(false);
    });
  };

  const loadRecent = async (offset = 0) => {
    const generation = workspaceGeneration.current;
    recentRequest.current?.abort();
    const controller = new AbortController();
    recentRequest.current = controller;
    setRecentForms([]);
    setRecentError(null);
    setRecentBusy(true);
    try {
      const result = await getOperatorRecentSubmissions(
        offset,
        controller.signal,
      );
      if (
        !controller.signal.aborted &&
        generation === workspaceGeneration.current
      ) {
        setRecentForms(result.items);
        setRecentOffset(offset);
        setRecentTotal(result.total);
      }
    } catch (reason) {
      if (
        controller.signal.aborted ||
        generation !== workspaceGeneration.current
      )
        return;
      setRecentError(
        reason instanceof Error ? reason.message : "Formlar yüklenemedi.",
      );
      if (isOperatorSessionError(reason)) throw reason;
    } finally {
      if (
        recentRequest.current === controller &&
        generation === workspaceGeneration.current
      )
        setRecentBusy(false);
    }
  };
  const openSubmitted = async (
    formId: string,
    expectedVersion: number,
    operatorId: string,
  ) => {
    if (pendingSaves.current > 0) return null;
    const generation = workspaceGeneration.current;
    setRecentBusy(true);
    setRecentError(null);
    try {
      const response = await reviseOperatorForm(formId, expectedVersion);
      if (generation !== workspaceGeneration.current) return null;
      assertOwnedBy(operatorId, response.form.ownerOperatorId);
      advanceGeneration();
      formRef.current = response.form;
      setForm(response.form);
      setSelectionVersion((value) => value + 1);
      setError(null);
      setNotice(
        "Form düzenlemeye açıldı. Değişikliklerden sonra yeniden gönderin.",
      );
      return response.form;
    } catch (reason) {
      if (generation !== workspaceGeneration.current) return null;
      setRecentError(
        reason instanceof Error
          ? reason.message
          : "Form düzenlemeye açılamadı.",
      );
      if (isOperatorSessionError(reason)) throw reason;
      return null;
    } finally {
      if (generation === workspaceGeneration.current) setRecentBusy(false);
    }
  };

  return {
    form,
    recentForms,
    recentOffset,
    recentTotal,
    recentBusy,
    recentError,
    loadRecent,
    openSubmitted,
    selectionVersion,
    error,
    notice,
    busy,
    save,
    resume,
    startNew,
  };
}

function isCreateOutcomeUncertain(reason: unknown) {
  if (!(reason instanceof ApiError)) return true;
  return (
    reason.code === "NETWORK_ERROR" ||
    reason.code === "INVALID_API_RESPONSE" ||
    reason.code.startsWith("HTTP_5") ||
    ["integration_error", "database_error", "unexpected_error"].includes(
      reason.errorType ?? "",
    )
  );
}

export function isOperatorSessionError(reason: unknown) {
  return (
    reason instanceof ApiError && reason.code === "OPERATOR_SESSION_REQUIRED"
  );
}
